import './application.css'
// The public brand, expressed in the same custom properties application.css
// just defined. Order is load-bearing: both sit at specificity (0,1,0), so the
// later file wins the tie. Never move this above application.css.
import '../shared/styles/portal-brand.css'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { ThemeProvider, type PersistDisplayPreferences } from '../shared/theme/ThemeProvider'
import { jsonFetch } from '../shared/lib/jsonFetch'
import { PortalBellContext } from '../shared/layouts/PortalShell'
import { NotificationBell } from '../pages/portal/Notifications/components/NotificationBell'

// Authenticated surface: persist changed display preferences server-side.
// shared/ThemeProvider stays surface-agnostic and never knows this endpoint.
// A plain JSON request, NOT an Inertia visit: the endpoint answers JSON and
// carries no X-Inertia header, which Inertia would surface as an error dialog.
// Fire-and-forget — localStorage already holds the applied value.
const persistDisplayPreferences: PersistDisplayPreferences = (prefs) => {
  void jsonFetch('/preferences/display', {
    method: 'PATCH',
    body: { display_preferences: prefs },
  })
}

void createInertiaApp({
  resolve: async (name) => {
    const pages = import.meta.glob<{ default: React.ComponentType }>([
      '../pages/portal/**/*.tsx',
      '!../pages/portal/**/*.{test,spec}.tsx',
      '!../pages/portal/Notifications/components/{NotificationBell,NotificationContext}.tsx',
    ])
    const loadPage = pages[`../pages/portal/${name}.tsx`]
    if (!loadPage) throw new Error(`Unknown portal page: ${name}`)
    return (await loadPage()).default
  },
  // Each page passes its own `title` to PortalShell, which renders <Head>. This
  // appends the service name so a tab or a bookmark identifies it, and supplies
  // the bare name for anything that has not set a title.
  title: (title) => (title ? `${title} – Mesterbrev` : 'Mesterbrev'),
  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },
  setup({ el, App, props }) {
    createRoot(el!).render(
      <React.StrictMode>
        {/* The public surface wears one fixed look: brand light, one palette,
            one typeface. `initial` is spread last in ThemeProvider's resolve(),
            so it beats both the server-shared preferences and localStorage — a
            visitor who once chose dark or another typeface on the internal
            workstation keeps that stored choice, simply inert here. Pinning the
            theme also means no prefers-color-scheme listener is ever attached
            on this surface, and reset() lands on the same three values. */}
        <ThemeProvider
          initial={{ theme: 'light', accent: 'authority', font: 'source' }}
          onPersist={persistDisplayPreferences}
        >
          <PortalBellContext.Provider value={<NotificationBell />}>
            <App {...props} />
          </PortalBellContext.Provider>
        </ThemeProvider>
      </React.StrictMode>,
    )
  },
})
